/**
 * Top navigation for Propitstop's umbrella marketing site (the new
 * `/`, `/used-cars`, `/estimate`, `/business` pages). Distinct from the
 * legacy `<Navbar>` which now belongs only to the `/service/*` sub-app.
 *
 * Design cues taken from the reference mockup: white background, clean
 * primary link row, prominent Login/Signup CTA, static "Bengaluru" city
 * pill (real switcher lands later per product decision 5b).
 */
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MapPin, Menu, X, ChevronDown } from 'lucide-react';

const NAV = [
  { name: 'Buy Cars', to: '/used-cars', testid: 'top-nav-buy-cars' },
  { name: 'Service', to: '/service', testid: 'top-nav-service' },
  { name: 'About Us', to: '/service/about', testid: 'top-nav-about' },
  { name: 'For Business', to: '/business', testid: 'top-nav-business' },
];

const isActive = (loc, to) => {
  if (to === '/service') return loc.pathname === '/service' || loc.pathname.startsWith('/service/');
  return loc.pathname === to;
};

export default function PropitstopNav() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  return (
    <nav data-testid="propitstop-nav" className="sticky top-0 z-50 bg-white/95 backdrop-blur-xl border-b border-zinc-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" data-testid="top-nav-logo" className="flex items-center gap-2 group">
            <img
              src="https://customer-assets.emergentagent.com/job_fcf8fe40-6235-4ae1-a5a5-ebd4002e0a55/artifacts/8jty1yq0_Gemini_Generated_Image_l9r4ujl9r4ujl9r4.png"
              alt="Propitstop"
              className="h-9 w-9 object-cover rounded-lg"
            />
            <div className="leading-tight">
              <div className="font-extrabold text-lg text-zinc-900 tracking-tight">propitstop</div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 font-medium">Your Car. Our Priority.</div>
            </div>
          </Link>

          {/* Desktop Links */}
          <div className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                className={`text-sm font-medium transition-colors ${
                  isActive(loc, n.to) ? 'text-blue-600' : 'text-zinc-700 hover:text-blue-600'
                }`}
              >
                {n.name}
              </Link>
            ))}
          </div>

          {/* Right cluster: City pill only (Login/Sign up removed per product decision) */}
          <div className="hidden lg:flex items-center gap-3">
            <button
              data-testid="top-nav-city"
              disabled
              title="Multi-city switcher coming soon"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-zinc-200 text-sm font-medium text-zinc-700 opacity-90 cursor-default"
            >
              <MapPin className="w-4 h-4 text-blue-500" /> Bengaluru <ChevronDown className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Mobile toggle */}
          <button
            className="lg:hidden p-2 text-zinc-700"
            onClick={() => setOpen(!open)}
            aria-label={open ? 'Close menu' : 'Open menu'}
            data-testid="top-nav-mobile-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="lg:hidden py-3 border-t border-zinc-200" data-testid="top-nav-mobile-menu">
            <div className="flex flex-col gap-1">
              {NAV.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  className={`px-3 py-2.5 rounded-lg text-sm font-medium ${
                    isActive(loc, n.to) ? 'text-blue-600 bg-blue-50' : 'text-zinc-700 hover:bg-zinc-100'
                  }`}
                >
                  {n.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
